import { useState, useMemo, useCallback, forwardRef, useEffect, useRef } from 'react';
import { pick } from '../../utils/pick';
import { getDefaultPropName, getOnChangeName } from '../../utils/PropNames';
import { jsx as _jsx } from "react/jsx-runtime";
function getComponentName(Component, fallback = 'Component') {
  if (!Component) {
    return fallback;
  }
  return Component.displayName || Component.name || Component.type && (Component.type.displayName || Component.type.name) || fallback;
}
const DEFAULT_FIELDS = ['value'];
function getDefaultProps(component, fields) {
  const defaultProps = component.defaultProps || {};
  return fields.reduce((defaults, field) => {
    if (Object.prototype.hasOwnProperty.call(defaultProps, field)) {
      defaults[getDefaultPropName(field)] = defaultProps[field];
    }
    return defaults;
  }, {});
}
export function Controllable(InputComponent, fields = DEFAULT_FIELDS) {
  const onChangeKeys = fields.map(getOnChangeName);
  const defaultPropsFromInput = getDefaultProps(InputComponent, fields);
  const ControlledComponent = /*#__PURE__*/forwardRef((props, ref) => {
    const mergedProps = useMemo(() => {
      return Object.assign({}, defaultPropsFromInput, props);
    }, [props]);
    const defaults = pick(mergedProps, fields.map(getDefaultPropName));

    // Determine which fields are controlled (have defined values)
    const isControlled = useMemo(() => {
      return fields.reduce((result, field) => {
        result[field] = mergedProps[field] !== undefined;
        return result;
      }, {});
    }, [mergedProps]);

    // Initialize state with default values
    const [state, setState] = useState(() => fields.reduce((initialState, field) => {
      initialState[field] = defaults[getDefaultPropName(field)];
      return initialState;
    }, {}));

    // Track previous controlled status to detect transitions
    const prevControlledRef = useRef({});

    // Initialize controlled status tracking on mount
    useEffect(() => {
      fields.forEach(field => {
        prevControlledRef.current[field] = isControlled[field];
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle field change events
    const handleChange = useCallback((field, evt) => {
      const value = evt.target.value;

      // Only update internal state if uncontrolled
      if (!isControlled[field]) {
        setState(prevState => Object.assign({}, prevState, {
          [field]: value
        }));
      }
      const onChange = mergedProps[getOnChangeName(field)];
      if (typeof onChange === 'function') {
        onChange(evt);
      }
    }, [mergedProps, isControlled]);

    // Handle transitions from controlled to uncontrolled
    useEffect(() => {
      const updates = fields.reduce((acc, field) => {
        const wasControlled = prevControlledRef.current[field];
        const isNowControlled = isControlled[field];

        // If transitioning from controlled to uncontrolled, reset to default value
        if (wasControlled && !isNowControlled) {
          acc[field] = defaults[getDefaultPropName(field)];
        }
        prevControlledRef.current[field] = isNowControlled;
        return acc;
      }, {});
      if (Object.keys(updates).length > 0) {
        setState(prev => Object.assign({}, prev, updates));
      }
    }, [isControlled, defaults]);

    // Create handlers for each field
    const handlers = useMemo(() => onChangeKeys.reduce((result, onChangeKey, i) => {
      result[onChangeKey] = evt => handleChange(fields[i], evt);
      return result;
    }, {}), [handleChange]);

    // Create final props for the wrapped component
    const finalProps = useMemo(() => {
      const result = fields.reduce((acc, field) => {
        acc[field] = isControlled[field] ? mergedProps[field] : state[field];
        return acc;
      }, {});

      // Add remaining props that aren't explicitly handled fields
      Object.keys(mergedProps).forEach(key => {
        if (!fields.includes(key) && !fields.map(getDefaultPropName).includes(key) && mergedProps[key] !== undefined) {
          result[key] = mergedProps[key];
        }
      });
      return Object.assign({}, result, handlers);
    }, [mergedProps, isControlled, state, handlers]);
    const componentProps = Object.assign({}, finalProps, {
      ref
    });
    return /*#__PURE__*/_jsx(InputComponent, Object.assign({}, componentProps));
  });
  ControlledComponent.displayName = `Controllable(${getComponentName(InputComponent)})`;
  return ControlledComponent;
}